import { paramToColor } from "./color";
import {
  definePreset,
  EDGE_DIRECTION_SCHEMA,
  setStatic,
  timing,
  WIPE_TO_REVEAL_DIR,
} from "./helpers";

import type { AnimationPreset, ParamSchema } from "./types";

/** Optional stroke tint while the draw animation runs; -1 (default)
 * inherits the element's stroke color (the panel's "Auto" button). */
const DRAW_COLOR_SCHEMA: ParamSchema = {
  key: "color",
  label: "Color",
  min: -1,
  max: 0xffffff,
  step: 1,
};

const tintStroke = (
  tl: gsap.core.Timeline,
  target: object,
  cfg: { params: Record<string, number> },
) => {
  const color = cfg.params.color ?? -1;
  if (color >= 0) {
    setStatic(tl, target, { strokeColor: paramToColor(color, 0) });
  }
};

export const drawPresets: AnimationPreset[] = [
  definePreset(
    "lineDraw",
    "Line Draw",
    "draw",
    {
      params: { color: -1 },
      schema: [DRAW_COLOR_SCHEMA],
      duration: 1500,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      // true stroke draw-on via the dash trick — the line (and arrowhead,
      // last) draws itself along the path; also works on shape outlines
      tintStroke(tl, target, cfg);
      tl.fromTo(
        target,
        { drawProgress: 0 },
        { drawProgress: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "lineErase",
    "Line Erase",
    "draw",
    {
      params: { color: -1 },
      schema: [DRAW_COLOR_SCHEMA],
      duration: 1500,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      tintStroke(tl, target, cfg);
      tl.fromTo(
        target,
        { drawProgress: 1 },
        { drawProgress: 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "drawOn",
    "Draw On",
    "draw",
    {
      params: { direction: 1, color: -1 },
      schema: [EDGE_DIRECTION_SCHEMA, DRAW_COLOR_SCHEMA],
      duration: 1500,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      tintStroke(tl, target, cfg);
      setStatic(tl, target, {
        revealDir:
          WIPE_TO_REVEAL_DIR[cfg.params.direction ?? 1] ??
          WIPE_TO_REVEAL_DIR[1],
      });
      tl.fromTo(
        target,
        { revealProgress: 0 },
        { revealProgress: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "drawOff",
    "Draw Off",
    "draw",
    {
      params: { direction: 1, color: -1 },
      schema: [EDGE_DIRECTION_SCHEMA, DRAW_COLOR_SCHEMA],
      duration: 1500,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      tintStroke(tl, target, cfg);
      setStatic(tl, target, {
        revealDir:
          WIPE_TO_REVEAL_DIR[cfg.params.direction ?? 1] ??
          WIPE_TO_REVEAL_DIR[1],
      });
      tl.fromTo(
        target,
        { revealProgress: 1 },
        { revealProgress: 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "drawSegment",
    "Draw Segment",
    "draw",
    {
      params: { length: 0.25, color: -1 },
      schema: [
        {
          key: "length",
          label: "Segment length",
          min: 0.05,
          max: 0.9,
          step: 0.05,
        },
        DRAW_COLOR_SCHEMA,
      ],
      duration: 1500,
      repeat: 2,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      // anime.js draw '0.25 0.75': a window of width `length` travels along
      // the stroke; both ends overshoot [0,1] and the renderer clamps, so
      // the segment enters at the start and exits at the end
      const w = cfg.params.length ?? 0.25;
      tintStroke(tl, target, cfg);
      tl.fromTo(
        target,
        { drawStart: -w, drawProgress: 0 },
        { drawStart: 1, drawProgress: 1 + w, ...timing(cfg) },
        0,
      );
    },
  ),
];
